import React, { useState, useEffect } from "react";
import styles from "./learningZoneInviteModal.module.css";
import axios from "axios";

const apiUrl = process.env.REACT_APP_URL;
axios.defaults.baseURL = `${apiUrl}`;
axios.defaults.withCredentials = true;

const LearningZoneInviteModal = ({
  isOpen,
  onClose,
  activeUsername,
  learningZoneId,
  learningZoneName,
}) => {
  /**
   * This component allows a learning zone creator to search for users on the platform, and invite them to join the learning zone.
   */

  const [searchedUsername, setSearchedUsername] = useState("");
  const [searchDone, setSearchDone] = useState(false);
  const [foundUser, setFoundUser] = useState(null);
  const [userMembershipStatus, setUserMembershipStatus] = useState(null);
  const [userInvited, setUserInvited] = useState(false);
  const [emptySearchTerm, setEmptySearchTerm] = useState(false);
  const [recentInvitationsCount, setRecentInvitationsCount] = useState(0);
  const [senderDetails, setSenderDetails] = useState(null);

  /* Get details of the active user (the user that wants to invite other users) */
  useEffect(() => {
    const fetchSenderDetails = async () => {
      try {
        const response = await axios.get(`/users/basic/${activeUsername}`);
        if (response.status === 200 && response.data.success) {
          setSenderDetails({
            firstName: response.data.firstName,
            lastName: response.data.lastName,
            imgLink: response.data.img_link,
          });
        }
      } catch (error) {
        console.error("Error fetching sender details:", error);
      }
    };

    fetchSenderDetails();
  }, [activeUsername]);

  /**
   * Handle change of the searched username
   */
  const handleSearchedUsernameChange = (e) => {
    setSearchedUsername(e.target.value);
    setEmptySearchTerm(false);
  };

  /**
   * Search the database for the entered username
   */
  const handleSearchBtnClick = async (e) => {
    e.preventDefault();
    setUserInvited(false);
    setSearchDone(false);
    setFoundUser(null);
    setUserMembershipStatus("notMember");
    setRecentInvitationsCount(0);

    if (!searchedUsername.trim()) {
      setEmptySearchTerm(true);
      return;
    }

    try {
      const userRes = await axios.get(
        `/search/users/exact?search_query=${searchedUsername}`
      );

      if (userRes.status === 200 && userRes.data.success) {
        setSearchDone(true);
        if (userRes.data.user.length > 0) {
          setFoundUser(userRes.data.user[0]);
          const username = userRes.data.user[0].username;
          const statusRes = await axios.get(
            `/learning_zones/list/status/${learningZoneId}/${username}`
          );

          if (statusRes.status === 200 && statusRes.data.success) {
            setUserMembershipStatus(statusRes.data.status);
            setRecentInvitationsCount(statusRes.data.recentInvitationsCount);
          }
        } else {
          setFoundUser(null);
        }
      }
    } catch (error) {
      console.error("Error searching for user:", error);
    }
  };

  /**
   * Close the modal and reset its fields
   */
  const handleCloseBtn = () => {
    setSearchedUsername("");
    setUserMembershipStatus(null);
    setFoundUser(null);
    setSearchDone(false);
    onClose();
  };

  /**
   * Invite a user to the learning zone, if possible according to max invitations policy
   */
  const handleInviteBtnClick = async (e) => {
    e.preventDefault();

    if (!foundUser || !senderDetails || recentInvitationsCount >= 4) {
      return;
    }

    try {
      let inviteRes;
      if (userMembershipStatus !== "invited") {
        inviteRes = await axios.post(
          `/learning_zones/list/invite/${learningZoneId}/${foundUser.username}`,
          { learningZoneName }
        );
      }

      setUserInvited(true);

      // Send notification to the invited user
      const notificationData = {
        notification_img_src: foundUser.img_link,
        message: `You have been invited to join the learning zone ${learningZoneName}`,
        type: "learningZoneInvite",
        sender_first_name: senderDetails.firstName,
        sender_last_name: senderDetails.lastName,
        sender_img_link: senderDetails.imgLink,
        receiver_username: foundUser.username,
        learning_zone_name: learningZoneName,
        learning_zone_id: learningZoneId,
      };

      await axios.post("/notifications", notificationData);
    } catch (error) {
      console.error("Error inviting user:", error);
    }
  };

  if (!isOpen) {
    return null;
  }

  /**
   * Close the modal when clicking outside it.
   */
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      handleCloseBtn();
    }
  };

  const BASE_URL = `${apiUrl}/`;

  return (
    <div className={styles.modalBackdrop} onClick={handleBackdropClick}>
      <div className={styles.modalContent}>
        <button className={styles.closeButton} onClick={handleCloseBtn}>
          &times;
        </button>
        <h2>Invite User to Join {learningZoneName}</h2>
        <form className={styles.searchForm}>
          <input
            type="text"
            name="username"
            placeholder="Enter username..."
            value={searchedUsername}
            onChange={handleSearchedUsernameChange}
          />

          <button onClick={handleSearchBtnClick}>Search</button>
        </form>

        {emptySearchTerm && <p>Please enter a username to search for</p>}

        {searchDone && foundUser && (
          <div className={styles.userResult}>
            <figure className={styles.userImgContainer}>
              <img
                src={
                  foundUser.img_link
                    ? `${BASE_URL}${foundUser.img_link.replace(/\\/g, "/")}`
                    : `${BASE_URL}default-image-path/default.jpg`
                }
                alt={foundUser.username}
              />
              <figcaption>
                <p>{foundUser.username}</p>
              </figcaption>
            </figure>
            {userMembershipStatus === "notMember" && !userInvited && (
              <button
                className={styles.inviteBtn}
                onClick={handleInviteBtnClick}
                disabled={recentInvitationsCount >= 4} // Disable button if the user has been invited four times
              >
                Invite
              </button>
            )}
            {userMembershipStatus === "member" && <p>Already Member</p>}
            {userMembershipStatus === "invited" && !userInvited && (
              <div>
                <p>Already Invited</p>
                {recentInvitationsCount < 4 ? (
                  <button
                    className={styles.inviteBtn}
                    onClick={handleInviteBtnClick}
                  >
                    Reinvite User
                  </button>
                ) : (
                  <p>
                    Cannot invite user more than four times in the last year.
                  </p>
                )}
              </div>
            )}
            {userInvited && <p>User invited</p>}
          </div>
        )}

        {searchDone && !foundUser && (
          <div>
            <p>User was not found - please try another username.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default LearningZoneInviteModal;
